import React, { useState, useMemo } from 'react';
import { AlertCircle, Check, X, Edit3, MessageSquare, Info } from 'lucide-react';

const TeacherCorrections = ({ originalText, corrections = [] }) => {
  const [hoveredCorrection, setHoveredCorrection] = useState(null);

  // Error type configurations
  const errorTypeConfig = {
    spelling: {
      color: '#dc2626',
      bgColor: '#fef2f2',
      icon: Edit3,
      label: 'Rechtschreibung'
    },
    grammar: {
      color: '#2563eb',
      bgColor: '#eff6ff',
      icon: AlertCircle,
      label: 'Grammatik'
    },
    punctuation: {
      color: '#ea580c',
      bgColor: '#fff7ed',
      icon: X,
      label: 'Zeichensetzung'
    },
    'word-choice': {
      color: '#7c3aed',
      bgColor: '#f5f3ff',
      icon: MessageSquare,
      label: 'Wortwahl'
    },
    'sentence-structure': {
      color: '#059669',
      bgColor: '#ecfdf5',
      icon: Edit3,
      label: 'Satzbau'
    },
    style: {
      color: '#6b7280',
      bgColor: '#f9fafb',
      icon: MessageSquare,
      label: 'Stil'
    },
    unknown: {
      color: '#6b7280',
      bgColor: '#f9fafb',
      icon: Info,
      label: 'Sonstige'
    }
  };

  const getErrorConfig = (type) => {
    return errorTypeConfig[type] || errorTypeConfig.unknown;
  };

  // SIMPLE: Just use the AI's corrections directly with validation
  const processedText = useMemo(() => {
    if (!originalText || !corrections || corrections.length === 0) {
      return [{ text: originalText, type: 'normal' }];
    }

    // Validate and filter corrections
    const validCorrections = corrections.filter(correction => {
      return (
        typeof correction.startIndex === 'number' &&
        typeof correction.endIndex === 'number' &&
        correction.startIndex >= 0 &&
        correction.endIndex > correction.startIndex &&
        correction.endIndex <= originalText.length &&
        correction.correctedText &&
        correction.correctedText !== originalText.slice(correction.startIndex, correction.endIndex)
      );
    });

    if (validCorrections.length === 0) {
      return [{ text: originalText, type: 'normal' }];
    }

    // Sort corrections by start position
    const sortedCorrections = validCorrections.sort((a, b) => a.startIndex - b.startIndex);

    // Create segments - SIMPLE approach
    const segments = [];
    let lastIndex = 0;

    sortedCorrections.forEach((correction, index) => {
      // Add text before this correction (unchanged)
      if (correction.startIndex > lastIndex) {
        segments.push({
          text: originalText.slice(lastIndex, correction.startIndex),
          type: 'normal'
        });
      }

      // Add the correction segment
      segments.push({
        text: originalText.slice(correction.startIndex, correction.endIndex),
        type: 'correction',
        correction: correction,
        id: index
      });

      lastIndex = correction.endIndex;
    });

    // Add remaining text after last correction
    if (lastIndex < originalText.length) {
      segments.push({
        text: originalText.slice(lastIndex),
        type: 'normal'
      });
    }

    return segments;
  }, [originalText, corrections]);

  // Simple segment rendering
  const renderSegment = (segment, index) => {
    if (segment.type === 'normal') {
      return <span key={index}>{segment.text}</span>;
    }

    const config = getErrorConfig(segment.correction.type);
    const isHovered = hoveredCorrection === segment.id;

    return (
      <span key={index} className="relative inline-block">
        {/* Simple underline - no complex styling */}
        <span
          className="cursor-help relative inline-block transition-colors duration-200"
          onMouseEnter={() => setHoveredCorrection(segment.id)}
          onMouseLeave={() => setHoveredCorrection(null)}
          style={{
            borderBottom: `2px solid ${config.color}`,
            backgroundColor: isHovered ? config.bgColor : 'transparent',
            padding: isHovered ? '1px 2px' : '0px',
            borderRadius: '2px'
          }}
        >
          {segment.text}
        </span>

        {/* Simple hover tooltip */}
        {isHovered && (
          <div
            className="absolute z-30 p-3 bg-white rounded-lg shadow-lg border border-gray-200 min-w-64 max-w-80"
            style={{
              top: '120%',
              left: '50%',
              transform: 'translateX(-50%)',
              animation: 'fadeIn 0.2s ease-out'
            }}
          >
            <div className="space-y-2">
              {/* Error type */}
              <div className="flex items-center space-x-2">
                <config.icon
                  className="w-4 h-4"
                  style={{ color: config.color }}
                />
                <span
                  className="font-semibold text-sm"
                  style={{ color: config.color }}
                >
                  {config.label}
                </span>
              </div>

              {/* Before/After */}
              <div className="border-t pt-2">
                <div className="flex items-center space-x-2 text-xs mb-1">
                  <X className="w-3 h-3 text-red-500" />
                  <span className="text-red-600">Fehler:</span>
                  <code className="bg-red-100 text-red-800 px-1 rounded">{segment.text}</code>
                </div>
                <div className="flex items-center space-x-2 text-xs">
                  <Check className="w-3 h-3 text-green-500" />
                  <span className="text-green-600">Korrektur:</span>
                  <code className="bg-green-100 text-green-800 px-1 rounded">{segment.correction.correctedText}</code>
                </div>
              </div>

              {/* Explanation */}
              {segment.correction.explanation && (
                <div className="border-t pt-2">
                  <p className="text-xs text-gray-700">
                    {segment.correction.explanation}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </span>
    );
  };

  // Simple statistics
  const stats = useMemo(() => {
    const typeCount = corrections.reduce((acc, corr) => {
      acc[corr.type] = (acc[corr.type] || 0) + 1;
      return acc;
    }, {});

    return {
      total: corrections.length,
      byType: typeCount
    };
  }, [corrections]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-bold text-gray-800">
          Korrigierter Text
        </h4>
        <div className="text-sm text-gray-600">
          {stats.total} {stats.total === 1 ? 'Korrektur' : 'Korrekturen'}
        </div>
      </div>

      {/* Simple legend */}
      {stats.total > 0 && (
        <div className="flex flex-wrap gap-3 p-3 bg-gray-50 rounded-lg text-xs">
          {Object.entries(stats.byType)
            .filter(([type, count]) => count > 0)
            .map(([type, count]) => {
              const config = getErrorConfig(type);
              return (
                <div key={type} className="flex items-center space-x-2">
                  <config.icon
                    className="w-3 h-3"
                    style={{ color: config.color }}
                  />
                  <span style={{ color: config.color }} className="font-medium">
                    {config.label} ({count})
                  </span>
                </div>
              );
            })}
        </div>
      )}

      {/* Original text with PRESERVED structure */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div
          className="text-gray-800 leading-relaxed"
          style={{
            fontSize: '16px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            lineHeight: '1.8',
            whiteSpace: 'pre-wrap' // CRITICAL: Preserves line breaks and spacing
          }}
        >
          {processedText.map((segment, index) => renderSegment(segment, index))}
        </div>
      </div>

      {/* Simple summary */}
      {stats.total > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800 font-medium mb-2">
            Zusammenfassung:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {Object.entries(stats.byType).map(([type, count]) => {
              const config = getErrorConfig(type);
              return (
                <div key={type} className="flex items-center space-x-2">
                  <config.icon
                    className="w-3 h-3"
                    style={{ color: config.color }}
                  />
                  <span>{config.label}: {count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No corrections */}
      {stats.total === 0 && (
        <div className="text-center p-6 bg-green-50 rounded-xl border border-green-200">
          <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <p className="text-green-800 font-medium">Keine Korrekturen erforderlich</p>
        </div>
      )}
    </div>
  );
};

export default TeacherCorrections;